import '../invoice.scss';

import moment from 'moment';
import { bool, func } from 'prop-types';
import React from 'react';
import { Col, FormGroup, Input, Label, Row } from 'reactstrap';

import Association from '../../../associations/Association';
import CampaignLink from '../../../campaigns/CampaignLink';
import { billingTypesEnum, delayReasonsOptions } from '../../../constants';
import MultiSelect from '../../../generic/MultiSelect';
import SingleSelect from '../../../generic/SingleSelect';
import TagInput from '../../../generic/TagInput';
import { getTooltipLabelWithText } from '../../../generic/Tooltip';
import SapInvoiceStatusBadge from '../../../invoices/SapInvoiceStatusBadge';
import { isAccounting, isSBO } from '../../../layout/Variant/Variant';
import {
  businessModels,
  escalationStages,
  invoiceBillingTypes,
  invoiceTypeOptions,
  salesChannels,
  salesChannelsEnum,
} from '../../../opportunities/constants';
import OpportunityLink from '../../../opportunities/OpportunityLink';
import { detailedInvoiceShape } from '../../../prop-types';
import QuoteLink from '../../../quotes/QuoteLink';
import { UI_SHORT_DATE_FORMAT } from '../../../utils/constants';
import HELP_TEXTS from '../../help-texts';
import { isInvoiceStatusExportedOrCanceled } from '../../utils';

function InvoiceFields({ model, onChange, readOnly, showOpportunity }) {
  const {
    purchaseOrderNumber,
    quote,
    campaign,
    opportunity,
    ownerId,
    ccInternalUserId,
    billingType,
    status,
    sapStatus,
    sapOrderNumber,
    sapInvoiceNumbers,
    sapReversalInvoiceNumbers,
    sapPaidAt,
    additionalStatusInformation,
    cancellationReason,
    type,
    salesChannel,
    escalationStage,
    merchants,
    delayReasons,
    accountSfId,
  } = model;

  const { businessModel } = opportunity;
  const inputProps = { onChange, readOnly };

  const merchantIds = merchants.map(item => item.id);

  const TooltipLabel = getTooltipLabelWithText(HELP_TEXTS);
  const isSelfService = salesChannel === salesChannelsEnum.SELF_SERVICE;
  const isWKZ = billingType === billingTypesEnum.WKZ;
  const isInternalOrWKZ = billingType === billingTypesEnum.INTERNAL || isWKZ;
  const canEditBillingEntity = !readOnly && (isAccounting() || isSBO());

  const renderMerchants = () => (
    <FormGroup row>
      <Label sm={12} md={4} for="iv-merchantIds">
        Merchants
      </Label>
      <Col sm={12} md={8} className="py-2">
        <Association
          id="iv-merchantIds"
          name="merchantIds"
          value={merchantIds}
          type="zmsamMerchant"
          {...inputProps}
          isMulti
          readOnly
        />
      </Col>
    </FormGroup>
  );

  return (
    <Row>
      <Col>
        {isSelfService && isInternalOrWKZ && (
          <FormGroup row>
            <Label sm={12} md={4} for="accountSfId">
              Billing Entity
            </Label>
            <Col sm={12} md={8} className="py-2">
              <Input
                id="iv-accountSfId"
                name="accountSfId"
                value={accountSfId}
                {...inputProps}
                disabled={!canEditBillingEntity}
              />
            </Col>
          </FormGroup>
        )}
        {isSelfService &&
          !isInternalOrWKZ &&
          (merchants[0]?.invoiceBillingEntity ? (
            <>
              {renderMerchants()}
              <FormGroup row>
                <Label sm={12} md={4} for="iv-invoiceBillingEntity">
                  Billing Entity
                </Label>
                <Col sm={12} md={8} className="py-2">
                  <Association
                    id="iv-invoiceBillingEntity"
                    name="invoiceBillingEntity"
                    value={merchants[0]?.invoiceBillingEntity?.id}
                    type="zmsamMerchant"
                    {...inputProps}
                    readOnly={!canEditBillingEntity}
                  />
                </Col>
              </FormGroup>
            </>
          ) : (
            renderMerchants()
          ))}
        {!isSelfService && (
          <FormGroup row>
            <Label sm={12} md={4} for="accountSfId">
              Billing Entity
            </Label>
            <Col sm={12} md={8} className="py-2">
              <Input
                id="iv-accountSfId"
                name="accountSfId"
                value={accountSfId}
                {...inputProps}
                disabled={!canEditBillingEntity}
              />
            </Col>
          </FormGroup>
        )}
        <FormGroup row>
          <Label sm={12} md={4} for="billingType">
            Billing Type
          </Label>
          <Col sm={12} md={8} className="py-2">
            <SingleSelect
              id="iv-billingType"
              name="billingType"
              value={billingType}
              options={invoiceBillingTypes}
              {...inputProps}
            />
          </Col>
        </FormGroup>
        {isWKZ && (
          <FormGroup row>
            <Label sm={12} md={4} for="businessModel">
              Business Model
            </Label>
            <Col sm={12} md={8} className="py-2">
              <SingleSelect
                id="iv-businessModel"
                name="businessModel"
                value={businessModel}
                options={businessModels}
                {...inputProps}
              />
            </Col>
          </FormGroup>
        )}
        <FormGroup row>
          <Label sm={12} md={4} for="iv-ccInternalUserId">
            CC Internal user
          </Label>
          <Col sm={12} md={8} className="py-2">
            <Association
              id="iv-ccInternalUserId"
              name="ccInternalUserId"
              value={ccInternalUserId}
              type="user"
              {...inputProps}
            />
          </Col>
        </FormGroup>
        <FormGroup row>
          <Label sm={12} md={4} for="iv-type">
            Type
          </Label>
          <Col sm={12} md={8} className="py-2">
            <SingleSelect
              id="iv-type"
              name="type"
              value={type}
              options={invoiceTypeOptions}
              {...inputProps}
              readOnly
            />
          </Col>
        </FormGroup>
        <FormGroup row>
          <Label sm={12} md={4} for="iv-salesChannel">
            Sales Channel
          </Label>
          <Col sm={12} md={8} className="py-2">
            <SingleSelect
              options={salesChannels}
              id="iv-salesChannel"
              name="salesChannel"
              value={salesChannel}
              {...inputProps}
              readOnly
            />
          </Col>
        </FormGroup>
        <FormGroup row>
          <Label sm={12} md={4} for="iv-escalationStage">
            Escalation Stage
          </Label>
          <Col sm={12} md={8} className="py-2">
            <SingleSelect
              options={escalationStages}
              id="iv-escalationStage"
              name="escalationStage"
              value={escalationStage}
              {...inputProps}
              readOnly
            />
          </Col>
        </FormGroup>
      </Col>
      <Col>
        <FormGroup row>
          <Label sm={12} md={4} for="iv-purchaseOrderNumber">
            PO Number
          </Label>
          <Col sm={12} md={8} className="py-2">
            <Input
              id="iv-purchaseOrderNumber"
              name="purchaseOrderNumber"
              value={purchaseOrderNumber}
              {...inputProps}
            />
          </Col>
        </FormGroup>
        <FormGroup row>
          <TooltipLabel
            helpKey="owner"
            text="Owner"
            colWidth={4}
            section="header"
            resource="inv"
          />
          <Col sm={12} md={8} className="py-2">
            <Association
              id="iv-ownerId"
              name="ownerId"
              value={ownerId}
              type="user"
              verbose
              {...inputProps}
              readOnly
            />
          </Col>
        </FormGroup>
        {showOpportunity && (
          <FormGroup row>
            <Label sm={12} md={4} for="iv-opportunityId">
              Opportunity
            </Label>
            <Col sm={12} md={8} className="py-2">
              <div className="text-truncate">
                <OpportunityLink model={opportunity} />
              </div>
            </Col>
          </FormGroup>
        )}
        {showOpportunity && quote && (
          <FormGroup row>
            <Label sm={12} md={4} for="iv-quoteId">
              Quote
            </Label>
            <Col sm={12} md={8} className="py-2">
              <div className="text-truncate">
                <QuoteLink model={quote} />
              </div>
            </Col>
          </FormGroup>
        )}
        {campaign && (
          <FormGroup row>
            <Label sm={12} md={4} for="iv-campaignId">
              Campaign
            </Label>
            <Col sm={12} md={8} className="py-2">
              <div className="text-truncate">
                <CampaignLink model={campaign} />
              </div>
            </Col>
          </FormGroup>
        )}
        {isInvoiceStatusExportedOrCanceled(status) && (
          <FormGroup row>
            <Label sm={12} md={4} for="iv-cancellationReason">
              Cancellation Reason
            </Label>
            <Col sm={12} md={8} className="py-2">
              <Input
                type="textarea"
                id="iv-cancellationReason"
                name="cancellationReason"
                value={cancellationReason}
                {...inputProps}
                readOnly
              />
            </Col>
          </FormGroup>
        )}
        <FormGroup row>
          <Label sm={12} md={4} for="iv-delayReasons">
            Delay Reasons
          </Label>
          <Col sm={12} md={8} className="py-2">
            <MultiSelect
              options={delayReasonsOptions}
              id="iv-delayReasons"
              name="delayReasons"
              value={delayReasons}
              {...inputProps}
            />
          </Col>
        </FormGroup>
      </Col>
      {isInvoiceStatusExportedOrCanceled(status) && (
        <Col>
          <FormGroup row>
            <Label sm={12} md={4} for="iv-sapStatus">
              SAP Status
            </Label>
            <Col sm={12} md={8} className="py-2">
              <div name="sapStatus">
                {sapStatus ? (
                  <SapInvoiceStatusBadge id="iv-sapStatus" status={sapStatus} />
                ) : (
                  <Input id="iv-sapStatus" name="sapStatus" readOnly />
                )}
              </div>
            </Col>
          </FormGroup>
          <FormGroup row>
            <Label sm={12} md={4} for="iv-sapOrderNumber">
              SAP Order Number
            </Label>
            <Col sm={12} md={8} className="py-2">
              <Input
                id="iv-sapOrderNumber"
                name="sapOrderNumber"
                value={sapOrderNumber || ''}
                {...inputProps}
                readOnly
              />
            </Col>
          </FormGroup>
          <FormGroup row>
            <Label sm={12} md={4} for="iv-sapInvoiceNumbers">
              SAP Invoice Numbers
            </Label>
            <Col sm={12} md={8} className="py-2">
              <TagInput
                id="iv-sapInvoiceNumbers"
                name="sapInvoiceNumbers"
                value={sapInvoiceNumbers}
                {...inputProps}
                readOnly
              />
            </Col>
          </FormGroup>
          <FormGroup row>
            <Label sm={12} md={4} for="iv-sapReversalInvoiceNumbers">
              SAP Reversal Invoice Numbers
            </Label>
            <Col sm={12} md={8} className="py-2">
              <TagInput
                id="iv-sapReversalInvoiceNumbers"
                name="sapReversalInvoiceNumbers"
                value={sapReversalInvoiceNumbers}
                {...inputProps}
                readOnly
              />
            </Col>
          </FormGroup>
          <FormGroup row>
            <Label sm={12} md={4} for="iv-sapPaidAt">
              SAP Paid On
            </Label>
            <Col sm={12} md={8} className="py-2">
              <Input
                id="iv-sapPaidAt"
                name="sapPaidAt"
                value={
                  (sapPaidAt &&
                    moment(sapPaidAt).format(UI_SHORT_DATE_FORMAT)) ||
                  ''
                }
                {...inputProps}
                readOnly
              />
            </Col>
          </FormGroup>
          <FormGroup row>
            <Label sm={12} md={4} for="iv-additionalStatusInformation">
              Additional Status Information
            </Label>
            <Col sm={12} md={8} className="py-2">
              <Input
                type="textarea"
                id="iv-additionalStatusInformation"
                name="additionalStatusInformation"
                value={additionalStatusInformation}
                {...inputProps}
                readOnly
              />
            </Col>
          </FormGroup>
        </Col>
      )}
    </Row>
  );
}

InvoiceFields.propTypes = {
  model: detailedInvoiceShape.isRequired,
  onChange: func,
  readOnly: bool,
  showOpportunity: bool,
};

InvoiceFields.defaultProps = {
  onChange: () => {},
  readOnly: false,
  showOpportunity: false,
};

export default InvoiceFields;
